(() => {
  const DE = (document.documentElement.lang || '').toLowerCase().indexOf('de') === 0;
  const FR = (document.documentElement.lang || '').toLowerCase().indexOf('fr') === 0;
  const article = document.querySelector('[data-yp-article]');
  if (!article) return;

  const content = article.querySelector('[data-yp-article-content]');
  const toc = article.querySelector('[data-yp-toc]');
  const tocList = article.querySelector('[data-yp-toc-list]');
  const progressBar = document.querySelector('[data-yp-reading-progress]');
  const copyButton = article.querySelector('[data-yp-copy-link]');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (content) {
    content.querySelectorAll('a[href]').forEach((link) => {
      const href = link.getAttribute('href');
      if (!href) return;
      const canonicalHref = href.replace(
        /^(https?:\/\/(?:www\.)?yarnpark\.com)?\/collections\/sport-fine(?=([?#]|$))/i,
        '$1/collections/2-sport-fine'
      );
      if (canonicalHref !== href) link.setAttribute('href', canonicalHref);
    });
  }

  const slugify = (text) => text
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N}\s-]/gu, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

  let scrollOffset = 105;
  const getScrollOffset = () => {
    const selectors = '.shopify-section--header,#shopify-section-header,[data-section-type="header"],.header,.nav-bar';
    let headerBottom = 0;
    document.querySelectorAll(selectors).forEach((element) => {
      const style = window.getComputedStyle(element);
      if (style.position !== 'fixed' && style.position !== 'sticky') return;
      const rect = element.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top <= 1) headerBottom = Math.max(headerBottom, rect.bottom);
    });
    return Math.max(Math.round(headerBottom + 20), 80);
  };
  const syncScrollOffset = () => {
    scrollOffset = getScrollOffset();
    document.documentElement.style.setProperty('--yp-scroll-offset', `${scrollOffset}px`);
  };
  syncScrollOffset();

  let headings = [];
  let links = [];
  let activeIndex = -1;
  let activeUpdateQueued = false;

  const keepActiveLinkVisible = (link) => {
    const nav = link.closest('nav');
    if (!nav || nav.scrollHeight <= nav.clientHeight) return;
    const navRect = nav.getBoundingClientRect();
    const linkRect = link.getBoundingClientRect();
    if (linkRect.top < navRect.top + 8) nav.scrollBy({ top: linkRect.top - navRect.top - 12, behavior: 'auto' });
    else if (linkRect.bottom > navRect.bottom - 8) nav.scrollBy({ top: linkRect.bottom - navRect.bottom + 12, behavior: 'auto' });
  };

  const setActiveLink = (index) => {
    if (!links.length || index < 0 || index === activeIndex) return;
    activeIndex = index;
    links.forEach((link, linkIndex) => {
      const isActive = linkIndex === index;
      link.classList.toggle('is-active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'location');
        keepActiveLinkVisible(link);
      } else link.removeAttribute('aria-current');
    });
  };

  const updateActiveLink = () => {
    activeUpdateQueued = false;
    if (!headings.length) return;
    const activationLine = scrollOffset + Math.min(window.innerHeight * 0.12, 100);
    let currentIndex = 0;
    headings.forEach((heading, index) => {
      if (heading.getBoundingClientRect().top <= activationLine) currentIndex = index;
    });
    if (window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 4) currentIndex = headings.length - 1;
    setActiveLink(currentIndex);
  };

  const requestActiveUpdate = () => {
    if (activeUpdateQueued) return;
    activeUpdateQueued = true;
    window.requestAnimationFrame(updateActiveLink);
  };

  const scrollToHeading = (heading, index, options = {}) => {
    syncScrollOffset();
    const top = window.scrollY + heading.getBoundingClientRect().top - scrollOffset;
    window.scrollTo({ top: Math.max(top, 0), behavior: options.behavior || (reducedMotion.matches ? 'auto' : 'smooth') });
    if (options.updateHistory !== false && window.location.hash !== `#${heading.id}`) window.history.pushState(null, '', `#${heading.id}`);
    setActiveLink(index);
  };

  const alignHashTarget = () => {
    if (!window.location.hash || !headings.length) return;
    let hashId;
    try { hashId = decodeURIComponent(window.location.hash.slice(1)); }
    catch (error) { hashId = window.location.hash.slice(1); }
    const index = headings.findIndex((heading) => heading.id === hashId);
    if (index < 0) return;
    window.requestAnimationFrame(() => scrollToHeading(headings[index], index, { behavior: 'auto', updateHistory: false }));
  };

  if (content && toc && tocList) {
    headings = Array.from(content.querySelectorAll('h2, h3'));
    const usedIds = new Set();
    headings.forEach((heading, index) => {
      let id = heading.id || slugify(heading.textContent) || `section-${index + 1}`;
      const baseId = id;
      let suffix = 2;
      while (usedIds.has(id) || document.querySelectorAll(`#${CSS.escape(id)}`).length > (heading.id ? 1 : 0)) {
        id = `${baseId}-${suffix}`;
        suffix += 1;
      }
      heading.id = id;
      usedIds.add(id);
      const item = document.createElement('li');
      item.dataset.level = heading.tagName === 'H3' ? '3' : '2';
      const link = document.createElement('a');
      link.href = `#${id}`;
      link.textContent = heading.textContent;
      link.addEventListener('click', (event) => {
        event.preventDefault();
        scrollToHeading(heading, index);
        if (window.matchMedia('(max-width: 649px)').matches) toc.open = false;
      });
      item.appendChild(link);
      tocList.appendChild(item);
    });
    links = Array.from(tocList.querySelectorAll('a'));
    if (!headings.length) toc.classList.add('is-empty');
    else {
      updateActiveLink();
      alignHashTarget();
      window.addEventListener('scroll', requestActiveUpdate, { passive: true });
      window.addEventListener('resize', () => { syncScrollOffset(); requestActiveUpdate(); });
      window.addEventListener('load', () => { syncScrollOffset(); requestActiveUpdate(); alignHashTarget(); });
      window.addEventListener('hashchange', alignHashTarget);
    }
  }

  const updateProgress = () => {
    if (!content || !progressBar) return;
    const rect = content.getBoundingClientRect();
    const start = window.scrollY + rect.top;
    const distance = Math.max(content.offsetHeight - window.innerHeight, 1);
    const progress = Math.min(Math.max((window.scrollY - start) / distance, 0), 1);
    progressBar.style.width = `${progress * 100}%`;
  };
  updateProgress();
  window.addEventListener('scroll', updateProgress, { passive: true });
  window.addEventListener('resize', updateProgress);

  if (copyButton) {
    copyButton.addEventListener('click', async () => {
      const originalLabel = copyButton.dataset.copyLabel || (DE ? 'Link kopieren' : FR ? 'Copier le lien' : 'Copy link');
      const copiedLabel = copyButton.dataset.copiedLabel || (DE ? 'Kopiert' : FR ? 'Lien copié' : 'Copied');
      try {
        await navigator.clipboard.writeText(window.location.href);
        copyButton.textContent = copiedLabel;
        window.setTimeout(() => { copyButton.textContent = originalLabel; }, 1800);
      } catch (error) {
        window.prompt(DE ? 'Diesen Link kopieren:' : 'Copy this link:', window.location.href);
      }
    });
  }
})();
